import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Trash2, ShieldCheck, Truck, Lock } from 'lucide-react';
import { useCart } from '../../hooks/useCart';
import { useStoreContext } from '../../contexts/StoreContext';
import Container from '../../components/layout/Container';
import Button from '../../components/common/Button';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import EmptyState from '../../components/common/EmptyState';
import QuantityStepper from '../../components/common/QuantityStepper';
import Card from '../../components/common/Card';
import { formatPrice } from '../../utils/priceCalculator';
import CouponField from '../../components/cart/CouponField';
import ShippingField from '../../components/cart/ShippingField';

const CartPage = () => {
  const { store } = useStoreContext();
  const {
    items,
    coupon,
    removeItem,
    updateQuantity,
    shipping,
    getSubtotal,
    getTotalDiscount,
    getCouponDiscount,
    getShippingCost,
    getTotalToPay,
    isEmpty,
  } = useCart();

  const totalDiscount = getTotalDiscount();
  const couponDiscount = getCouponDiscount();
  const shippingCost = getShippingCost();
  const hasResellerView = totalDiscount > 0;

  // Rotas contextuais: dentro de storefront mantém prefixo /fornecedor/:slug.
  const storeBase = store?.slug ? `/fornecedor/${store.slug}` : '';
  const catalogHref = storeBase ? `${storeBase}/catalogo` : '/catalogo';
  const checkoutHref = storeBase ? `${storeBase}/checkout` : '/checkout';

  if (isEmpty) {
    return (
      <Container>
        <Card className="mt-8">
          <EmptyState
            icon={ShoppingBag}
            title="Sua sacola está vazia"
            description="Adicione produtos à sacola pra continuar."
            action={
              <Link to={catalogHref}>
                <Button>Ver catálogo</Button>
              </Link>
            }
          />
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold text-app-text">Sacola</h1>
        <Link to={catalogHref} className="text-sm font-medium text-primary hover:underline">
          ← Continuar comprando
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ul className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <li key={item.id}>
              <Card className="p-4 flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-app-surface-hover">
                  <ImageWithFallback
                    src={item.image}
                    alt={item.productName}
                    fallbackText={item.productName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-app-text truncate">{item.productName}</h3>
                      {item.variationName && (
                        <p className="text-sm text-app-text-secondary">{item.variationName}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="p-1.5 rounded text-app-text-secondary hover:bg-error/10 hover:text-error transition-colors"
                      aria-label="Remover item"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
                    <QuantityStepper
                      value={item.quantity}
                      onChange={(v) => updateQuantity(item.id, v)}
                      size="sm"
                    />
                    <div className="text-right">
                      <p className="text-xs text-app-text-secondary">
                        {item.unitDiscount > 0 ? 'Pague apenas' : 'Você paga'}
                      </p>
                      <p className={`font-bold ${item.unitDiscount > 0 ? 'text-success' : 'text-app-text'}`}>
                        {formatPrice((item.unitPrice ?? item.price) * item.quantity)}
                      </p>
                      {item.unitDiscount > 0 && (
                        <p className="text-xs text-app-text-secondary line-through">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </li>
          ))}
        </ul>

        <div>
          <Card className="p-6 lg:sticky lg:top-20">
            <h2 className="text-lg font-bold text-app-text mb-4">Resumo</h2>
            <dl className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <dt className="text-app-text-secondary">Subtotal:</dt>
                <dd>{formatPrice(getSubtotal())}</dd>
              </div>
              {hasResellerView && (
                <div className="flex justify-between text-success">
                  <dt>Sua comissão (desconto):</dt>
                  <dd className="font-semibold">+{formatPrice(totalDiscount)}</dd>
                </div>
              )}
              {couponDiscount > 0 && (
                <div className="flex justify-between text-success">
                  <dt>Cupom {coupon?.code}:</dt>
                  <dd className="font-semibold">-{formatPrice(couponDiscount)}</dd>
                </div>
              )}
              {coupon?.type === 'FREE_SHIPPING' && (
                <div className="flex justify-between text-success">
                  <dt>Cupom {coupon.code}:</dt>
                  <dd className="font-semibold">Frete grátis</dd>
                </div>
              )}
              {shipping && (
                <div className="flex justify-between">
                  <dt className="text-app-text-secondary">Frete ({shipping.name}):</dt>
                  <dd>
                    {shippingCost > 0
                      ? formatPrice(shippingCost)
                      : <span className="text-success">Grátis</span>}
                  </dd>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-app-border font-bold text-lg">
                <dt>Total a pagar:</dt>
                <dd className="text-primary">{formatPrice(getTotalToPay())}</dd>
              </div>
            </dl>

            <div className="mb-4 space-y-3">
              <ShippingField />
              <CouponField />
            </div>

            <Link to={checkoutHref}>
              <Button fullWidth size="lg">
                Finalizar compra
              </Button>
            </Link>

            <ul className="mt-4 space-y-2 text-xs text-app-text-secondary">
              <li className="flex items-center gap-2">
                <Lock size={14} className="text-success" />
                Pagamento criptografado
              </li>
              <li className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-success" />
                Compra protegida
              </li>
              <li className="flex items-center gap-2">
                <Truck size={14} className="text-success" />
                Entrega rastreada
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </Container>
  );
};

export default CartPage;
